// RGR01 반품입고 조회 — GET /api/v1/return/inbounds (moms-be ReturnArrivalNoticeController.kt:86-100).
// 응답 행(ReturnArrivalNoticeDto)을 생성본 표의 dataIndex(1.0 컬럼명)로 옮긴다.
import { useQuery, UseQueryResult } from '@tanstack/react-query'
import qs from 'qs'
import request from '@/utils/request'

/**
 * 조회 응답 1행 — BE `ReturnArrivalNoticeDto`.
 *
 * 화면에 안 보이는 필드까지 적는다. 저장(request-inbound)은 기존 행을 고칠 때 요청에 없는 필드를
 * null 로 덮어쓴다(`ArrivalNoticeService.update` — ref·gds·etc·lot·ret 를 요청값으로 통째로 교체).
 * 그래서 저장 본문은 이 원본을 행에 들고 있다가 되돌려 보낸다(`_src`).
 */
export interface ReturnArrivalNoticeItem {
  id: number
  sprNm: string | null
  shipperName: string | null
  serviceType: string | null
  ordType: string | null
  plnDate: string | null
  ordNo: string | null
  ordOptNo: string | null
  ordQty: number | null
  barcode: string | null
  skuId: string | null
  optionCode: number | null
  brdNm: string | null
  stlNo: string | null
  gdsCd: string | null
  gdsNm: string | null
  gdsOpt: string | null
  gdsGroup1: string | null
  gdsAttr1: string | null
  gdsColor: string | null
  salChannel: string | null
  procNm: string | null
  whNm: string | null
  whArea: string | null
  trsType: string | null
  trsInvoiceNo: string | null
  trsNumber: string | null
  trsLspNm: string | null
  trsMsg: string | null
  trsNm: string | null
  trsPhone: string | null
  trsAddr: string | null
  rsnCode: string | null
  rtnType: string | null
  rlChannel: string | null
  claimId: string | null
  jobCommentForInvoice: string | null
  finalResponsibility: string | null
  refNo1: string | null
  refNo2: string | null
  refNo3: string | null
  refNo4: string | null
  refNo5: string | null
  lotAttr1: string | null
  lotAttr2: string | null
  lotAttr3: string | null
  lotAttr4: string | null
  lotAttr5: string | null
  lotAttr6: string | null
  etc1: string | null
  etc2: string | null
  etc3: string | null
  etc4: string | null
  etc5: string | null
  etc6: string | null
  fileId: string | null
  incoterms: string | null
  uid: string | null
  comNm: string | null
  apPrice: number | null
  asnSeq: number | null
  flag: boolean
  apiFlag: boolean
  ifReq: boolean
  /** 형식이 전역 Jackson 설정을 따라 확인하지 못했다 — 문자열·[y,M,d] 배열 둘 다 받는다 */
  manufacturedDate?: unknown
  expirationDate?: unknown
  insDate: string | null
  insBy: string | null
  updDate: string | null
  updBy: string | null
}

/** 표 행 — 생성본 COLUMNS 의 dataIndex(1.0 컬럼명) + 화면 전용 메타(`_` 접두) */
export interface ReturnInboundRow {
  _rowKey: string
  /** 저장(items[].id)·삭제·확정(ids) 대상 식별자. 신규 행은 0 */
  _id: number
  /** 엑셀 업로드 신규 행 — id 없이 생성 요청으로 나간다 */
  _isNew?: boolean
  /** 첨부·동영상 목록 조회 키 */
  _fileId: string | null
  /** 조회 원본 — 저장 때 화면에 없는 필드를 되돌려 보낸다. 신규 행은 null */
  _src: ReturnArrivalNoticeItem | null
  service_type: string
  shipper_name: string
  SPR_NM: string
  ORD_OPT_NO: string
  TRS_TYPE: string
  LOT_ATTR5: string
  sku_id: string
  LOT_ATTR4: string
  PLN_DATE: string
  BARCODE: string
  GDS_NM: string
  ORD_QTY: number | string
  option_code: string
  WH_NM: string
  STL_NO: string
  GDS_OPT: string
  GDS_GROUP1: string
  BRD_NM: string
  INS_DATE: string
  INS_BY: string
  UPD_DATE: string
  UPD_BY: string
  REF_NO1: string
  REF_NO2: string
  PROC_NM: string
  UID: string
  REF_NO3: string
  TRS_NM: string
  TRS_NUMBER: string
  TRS_PHONE: string
  TRS_ADDR: string
  SAL_CHANNEL: string
  TRS_INVOICE_NO: string
  TRS_LSP_NM: string
  ETC1: string
  ETC2: string
  ETC3: string
  ETC4: string
  ETC5: string
  ETC6: string
  /** 첨부 개수 — 파일 목록 조회 결과로 화면이 채운다 */
  ATCH_FILE: number
  /** 동영상 개수 — 파일 목록 조회 결과로 화면이 채운다 */
  VIDEO: number
  claim_id: string
  final_responsibility: string
}

const str = (v: string | number | null | undefined): string => (v == null ? '' : String(v))

/** 'yyyyMMdd'·'yyyy-MM-dd…' → 'yyyy-MM-dd' (표 datepicker 셀 형식). 8자리를 못 읽으면 원문 */
export const toDashDate = (v: string | null | undefined): string => {
  if (!v) return ''
  const d = v.replace(/[^0-9]/g, '')
  return d.length >= 8 ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` : v
}

/** 'yyyyMMddHHmmss' 류 → 'yyyy-MM-dd HH:mm:ss'. 시각이 없으면 날짜만 */
const toDateTime = (v: string | null | undefined): string => {
  if (!v) return ''
  const d = v.replace(/[^0-9]/g, '')
  if (d.length < 14) return toDashDate(v)
  return `${toDashDate(d)} ${d.slice(8, 10)}:${d.slice(10, 12)}:${d.slice(12, 14)}`
}

export const toRow = (item: ReturnArrivalNoticeItem): ReturnInboundRow => ({
  _rowKey: `r-${item.id}`,
  _id: item.id,
  _fileId: item.fileId,
  _src: item,
  service_type: str(item.serviceType),
  shipper_name: str(item.shipperName),
  SPR_NM: str(item.sprNm),
  ORD_OPT_NO: str(item.ordOptNo),
  TRS_TYPE: str(item.trsType),
  // 거래유형·불량구분 — 원장 코드 그대로(MSSB2B·ERR). 빈 값(null·'')이 온라인·양품이다(MOMS_COMBO_INFO 코드 'NULL')
  LOT_ATTR5: str(item.lotAttr5),
  sku_id: str(item.skuId),
  LOT_ATTR4: str(item.lotAttr4),
  PLN_DATE: toDashDate(item.plnDate),
  BARCODE: str(item.barcode),
  GDS_NM: str(item.gdsNm),
  // 빈 수량은 null 이 아니라 '' 다 — amountInput 은 null 이면 입력창 대신 '-' 를 그려 고칠 수 없다(GR01 useGetInboundList 주석)
  ORD_QTY: item.ordQty ?? '',
  option_code: str(item.optionCode),
  WH_NM: str(item.whNm),
  STL_NO: str(item.stlNo),
  GDS_OPT: str(item.gdsOpt),
  GDS_GROUP1: str(item.gdsGroup1),
  BRD_NM: str(item.brdNm),
  INS_DATE: toDateTime(item.insDate),
  INS_BY: str(item.insBy),
  UPD_DATE: toDateTime(item.updDate),
  UPD_BY: str(item.updBy),
  REF_NO1: str(item.refNo1),
  REF_NO2: str(item.refNo2),
  PROC_NM: str(item.procNm),
  UID: str(item.uid),
  REF_NO3: str(item.refNo3),
  TRS_NM: str(item.trsNm),
  TRS_NUMBER: str(item.trsNumber),
  TRS_PHONE: str(item.trsPhone),
  TRS_ADDR: str(item.trsAddr),
  SAL_CHANNEL: str(item.salChannel),
  TRS_INVOICE_NO: str(item.trsInvoiceNo),
  TRS_LSP_NM: str(item.trsLspNm),
  ETC1: str(item.etc1),
  ETC2: str(item.etc2),
  ETC3: str(item.etc3),
  ETC4: str(item.etc4),
  ETC5: str(item.etc5),
  ETC6: str(item.etc6),
  ATCH_FILE: 0,
  VIDEO: 0,
  claim_id: str(item.claimId),
  final_responsibility: str(item.finalResponsibility),
})

/**
 * 바코드가 바뀐 행은 SKUID 를 비우고, 원본 바코드로 되돌리면 원본 SKUID 를 되살린다(LOGISPRD-12866 반품·입고 화면 규칙).
 * 옛 바코드의 SKUID 가 새 바코드 옆에 남아 보이지 않게 한다. RGR01 은 SKUID 칸을 직접 고칠 수 있어(데모 input)
 * rgr04 처럼 늘 원본으로 되돌리지 않고 **바코드가 바뀐 순간에만** 맞춘다. 신규 행은 원본이 없어 비우기만 한다.
 */
export const followBarcode = (prev: ReturnInboundRow, next: ReturnInboundRow): ReturnInboundRow => {
  if (next.BARCODE === prev.BARCODE) return next
  const original = next._src
  return { ...next, sku_id: original && next.BARCODE === str(original.barcode) ? str(original.skuId) : '' }
}

/** 엑셀 업로드 신규 행의 빈 값 */
export const emptyReturnInboundRow = (rowKey: string): ReturnInboundRow => ({
  _rowKey: rowKey,
  _id: 0,
  _isNew: true,
  _fileId: null,
  _src: null,
  service_type: '',
  shipper_name: '',
  SPR_NM: '',
  ORD_OPT_NO: '',
  TRS_TYPE: '',
  LOT_ATTR5: '',
  sku_id: '',
  LOT_ATTR4: '',
  PLN_DATE: '',
  BARCODE: '',
  GDS_NM: '',
  ORD_QTY: '',
  option_code: '',
  WH_NM: '',
  STL_NO: '',
  GDS_OPT: '',
  GDS_GROUP1: '',
  BRD_NM: '',
  INS_DATE: '',
  INS_BY: '',
  UPD_DATE: '',
  UPD_BY: '',
  REF_NO1: '',
  REF_NO2: '',
  PROC_NM: '',
  UID: '',
  REF_NO3: '',
  TRS_NM: '',
  TRS_NUMBER: '',
  TRS_PHONE: '',
  TRS_ADDR: '',
  SAL_CHANNEL: '',
  TRS_INVOICE_NO: '',
  TRS_LSP_NM: '',
  ETC1: '',
  ETC2: '',
  ETC3: '',
  ETC4: '',
  ETC5: '',
  ETC6: '',
  ATCH_FILE: 0,
  VIDEO: 0,
  claim_id: '',
  final_responsibility: '',
})

/** 조회 조건 — 화면 필터에서 만든다. 날짜는 yyyyMMdd */
export interface ReturnInboundParams {
  /** 화주명 — BE 가 shipper_name IN 으로 거른다(화주코드 아님) */
  shipperNames: string[]
  serviceTypes: string[]
  plnDateFrom?: string
  plnDateTo?: string
  ordOptNos?: string[]
  /** 송장 — 1.0 RGR01 필터 설정(prod MOMS_PROG_INFO)이 REF_NO2 '송장번호'였다. 데모 키 REF_NO3 는 오기 */
  refNo2s?: string[]
  trsInvoiceNos?: string[]
  /** 불량구분·거래유형 — 콤보 코드. 'NULL'(양품·온라인)은 BE 가 빈 값 조건으로 읽는다(LOGISPRD-13566) */
  lotAttr4?: string
  lotAttr5?: string
}

/**
 * 주문유형 — 1.0 RGR01.js onhandleSearch 가 늘 넣던 조건(`ORD_TYPE='반품'`). 없으면 같은 화주의 일반입고 행이 섞인다.
 * BE 파라미터는 LOGISPRD-13043 에서 열린다 — 그 전에는 BE 가 모르는 파라미터라 무시한다.
 */
export const RETURN_ORD_TYPE = '반품'

/**
 * 반품 행만 표에 올린다. 조회 API 가 주문유형으로 거르지 않아(LOGISPRD-13043 전) 같은 화주의 일반입고 행이 섞이는데,
 * 그 행을 저장하면 BE 가 orderType '반품' 으로 MENU_CD·ORD_TYPE 을 덮어써 일반입고에서 사라진다.
 * 1.0 의 ORD_TYPE='반품' 조건과 같은 결과라 13043 머지 뒤에도 안전장치로 둔다. 응답 ordType 은 OrderType.enumCode(표시명)다.
 */
export const isReturnItem = (item: ReturnArrivalNoticeItem): boolean => item.ordType === RETURN_ORD_TYPE

/** 필수 조건 — BE 가 화주명·물류서비스유형 중 하나를 요구한다(없으면 400) */
export const hasShipperCondition = (p: ReturnInboundParams): boolean => p.shipperNames.length > 0 || p.serviceTypes.length > 0

/** 조회·엑셀 공용 필터 본문. 빈 값은 싣지 않는다 */
export const toSearchFilter = (p: ReturnInboundParams): Record<string, unknown> => ({
  ...(p.shipperNames.length ? { shipper: p.shipperNames } : {}),
  ...(p.serviceTypes.length ? { logisticsServiceTypes: p.serviceTypes } : {}),
  ...(p.plnDateFrom ? { plnDateFrom: p.plnDateFrom } : {}),
  ...(p.plnDateTo ? { plnDateTo: p.plnDateTo } : {}),
  ...(p.ordOptNos?.length ? { ordOptNo: p.ordOptNos } : {}),
  ...(p.refNo2s?.length ? { refNo2: p.refNo2s } : {}),
  ...(p.trsInvoiceNos?.length ? { trsInvoiceNo: p.trsInvoiceNos } : {}),
  ...(p.lotAttr4 ? { lotAttr4: p.lotAttr4 } : {}),
  ...(p.lotAttr5 ? { lotAttr5: p.lotAttr5 } : {}),
  ordType: RETURN_ORD_TYPE,
})

const fetchReturnInbounds = async (p: ReturnInboundParams): Promise<ReturnArrivalNoticeItem[]> => {
  // 화주명에 쉼표가 들어갈 수 있어 key=a&key=b 로 편다(commonFile/apis.ts 선례)
  const query = qs.stringify(toSearchFilter(p), { arrayFormat: 'repeat', skipNulls: true })
  const res = await request.get<ApiResponseV2<{ items: ReturnArrivalNoticeItem[] }>>(`/api/v1/return/inbounds?${query}`)
  return res.data?.items ?? []
}

/** 맨 앞 'rgr16' 은 라우트 키 — 탭을 닫으면 MenuOnSessionWrap 이 메뉴코드가 든 캐시를 지운다 */
export const returnInboundQueryKeys = {
  all: ['rgr16', 'RETURN_INBOUND'] as const,
  list: (p: ReturnInboundParams | null) => [...returnInboundQueryKeys.all, 'LIST', p] as const,
  /** 화주코드 서치헬퍼 후보 — 모달 [조회]·Enter 로 적용된 조건 */
  helperShipper: (q: { type: string; name: string } | null) => [...returnInboundQueryKeys.all, 'HELPER_SHIPPER', q] as const,
  /** 바코드 서치헬퍼 후보 — 연 행의 화주 + 모달 조건 */
  helperBarcode: (q: { shipperCode: string; brandName: string; itemCode: string } | null) => [...returnInboundQueryKeys.all, 'HELPER_BARCODE', q] as const,
  /** 정불구분 칸 설정 — 보는 사람의 화주(LOGISPRD-16194) */
  etc5Setting: (viewerShipperCode: string) => [...returnInboundQueryKeys.all, 'ETC5_SETTING', viewerShipperCode] as const,
}

/** 조건이 없으면(null 또는 화주 조건 없음) 조회하지 않는다 — BE 400 을 미리 막는다 */
export const useReturnInboundList = (params: ReturnInboundParams | null): UseQueryResult<ReturnArrivalNoticeItem[], Error> =>
  useQuery({
    queryKey: returnInboundQueryKeys.list(params),
    queryFn: () => fetchReturnInbounds(params as ReturnInboundParams),
    enabled: Boolean(params && hasShipperCondition(params)),
  })

/** 바코드 헬퍼 1행 — GET /api/v1/master/products/barcodes (MasterProductBarcodeOptionResponse) */
export interface BarcodeOption {
  barcode: string
  itemCode: string | null
  styleNumber: string | null
  brandName: string | null
}

/** 바코드 헬퍼 조회 — 1.0 BARCODE 서치헬퍼 이관(LOGISPRD-9955). 브랜드·상품코드는 부분일치 */
export const fetchBarcodeOptions = async (p: { shipperCode: string; brandName?: string; itemCode?: string }): Promise<BarcodeOption[]> => {
  const query = qs.stringify({ shipperCode: p.shipperCode, brandName: p.brandName || undefined, itemCode: p.itemCode || undefined })
  const res = await request.get<ApiResponseV2<{ items: BarcodeOption[] }>>(`/api/v1/master/products/barcodes?${query}`)
  return res.data?.items ?? []
}
